import { SchoolEvent, SchoolEventSchoolScore, SchoolEventTeamScore } from 'models/event';

export type SchoolEventOutcome = 'W' | 'D' | 'L' | '';

const stringId = (value: unknown): string => value == null ? '' : String(value);

function scoreValue(score?: SchoolEventTeamScore | SchoolEventSchoolScore): number | undefined {
   const value = score?.richScore?.result ?? score?.score;
   return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function compareScores(
   event: SchoolEvent,
   ownScore?: SchoolEventTeamScore | SchoolEventSchoolScore,
   opponentScore?: SchoolEventTeamScore | SchoolEventSchoolScore
): SchoolEventOutcome {
   const own = scoreValue(ownScore);
   const opponent = scoreValue(opponentScore);
   if (own === undefined || opponent === undefined) return '';
   if (own === opponent) return 'D';
   const lowerScoreWins = event.sport?.scoring === 'LESS_SCORES';
   const ownWins = lowerScoreWins ? own < opponent : own > opponent;
   return ownWins ? 'W' : 'L';
}

export function getSchoolEventOutcome(event: SchoolEvent, schoolId: string): SchoolEventOutcome {
   const activeSchoolId = stringId(schoolId);
   const schoolScores = event.results?.schoolScore || [];
   const ownSchoolScore = schoolScores.find(score => stringId(score.schoolId) === activeSchoolId);
   if (ownSchoolScore?.isWinner) return 'W';
   if (ownSchoolScore && schoolScores.some(score => stringId(score.schoolId) !== activeSchoolId && score.isWinner)) return 'L';
   if (ownSchoolScore && schoolScores.length > 1 && !schoolScores.some(score => score.isWinner)) return 'D';

   const teams = Array.from(new Map(
      [...(event.teamsData || []), ...(event.embeddedTeams || [])].map(team => [
         stringId('id' in team ? team.id : team._id),
         team
      ])
   ).values());
   const ownTeamIds = new Set(
      teams
         .filter(team => stringId(team.schoolId) === activeSchoolId)
         .map(team => stringId('id' in team ? team.id : team._id))
         .filter(Boolean)
   );
   if (ownTeamIds.size === 0) return '';

   const teamScores = event.results?.teamScore || [];
   const ownScores = teamScores.filter(score => ownTeamIds.has(stringId(score.teamId)));
   const opponentScores = teamScores.filter(score => !ownTeamIds.has(stringId(score.teamId)));
   if (ownScores.some(score => score.isWinner)) return 'W';
   if (opponentScores.some(score => score.isWinner)) return 'L';

   const winnerTeamId = stringId(event.matchResult?.winnerTeamId);
   if (winnerTeamId) return ownTeamIds.has(winnerTeamId) ? 'W' : 'L';

   // Legacy external fixtures may store one side as a team score and the
   // other as a school score. Match the same participant pairing used by the
   // public result formatter instead of assuming both scores share an array.
   if (event.eventType === 'EXTERNAL_SCHOOLS') {
      if (teams.length === 1) {
         const onlyTeam = teams[0];
         const onlyTeamId = stringId('id' in onlyTeam ? onlyTeam.id : onlyTeam._id);
         const onlyTeamScore = teamScores.find(score => stringId(score.teamId) === onlyTeamId);
         const teamBelongsToSchool = stringId(onlyTeam.schoolId) === activeSchoolId;
         const ownScore = teamBelongsToSchool
            ? onlyTeamScore
            : schoolScores.find(score => stringId(score.schoolId) === activeSchoolId);
         const opponentScore = teamBelongsToSchool
            ? schoolScores.find(score => stringId(score.schoolId) !== activeSchoolId)
            : onlyTeamScore;
         const outcome = compareScores(event, ownScore, opponentScore);
         if (outcome) return outcome;
      } else if (teams.length >= 2) {
         const ownTeam = teams.find(team => stringId(team.schoolId) === activeSchoolId);
         const ownTeamId = ownTeam && stringId('id' in ownTeam ? ownTeam.id : ownTeam._id);
         if (ownTeamId) {
            const ownScore = teamScores.find(score => stringId(score.teamId) === ownTeamId);
            const opponentScore = teamScores.find(score => stringId(score.teamId) !== ownTeamId);
            const outcome = compareScores(event, ownScore, opponentScore);
            if (outcome) return outcome;
         }
      }
   }

   if (ownScores.length === 1 && opponentScores.length === 1) {
      const outcome = compareScores(event, ownScores[0], opponentScores[0]);
      if (outcome) return outcome;
      // Published legacy draws normally have two team-score rows with both
      // isWinner flags false, even when their numeric score was not revived.
      if (!ownScores[0].isWinner && !opponentScores[0].isWinner) return 'D';
   }

   if (event.matchResult?.regulation) {
      const { home, away } = event.matchResult.regulation;
      if (home === away) return 'D';
   }

   return '';
}
